import type { Inertia } from "../../types/scene";
import { xmlEscapeAttr } from "./xmlGenerationHelpers";

const TENDON_DEFAULT_ATTR_ORDER = [
  "limited",
  "range",
  "springlength",
  "width",
  "rgba",
  "material",
  "frictionloss",
  "damping",
  "stiffness",
];

const compareTendonDefaultAttr = ([keyA]: [string, string], [keyB]: [string, string]): number => {
  const indexA = TENDON_DEFAULT_ATTR_ORDER.indexOf(keyA);
  const indexB = TENDON_DEFAULT_ATTR_ORDER.indexOf(keyB);
  if (indexA !== -1 || indexB !== -1) {
    return (
      (indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA) -
      (indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB)
    );
  }
  return keyA.localeCompare(keyB);
};

export const DEFAULT_INERTIAL: Omit<Inertia, "uuid" | "name"> = {
  mass: 1.0,
  diagonal: [0.001, 0.001, 0.001],
  position: [0, 0, 0],
  orientation: [1, 0, 0, 0],
};

export const buildTendonDefaultClassXml = ({
  tendonDefaultAttributesByClass,
  tendonFixedChunks,
}: {
  tendonDefaultAttributesByClass: Map<string, Record<string, string>>;
  tendonFixedChunks: string[];
}): string => {
  const referencedTendonDefaultClasses = new Set<string>();
  tendonFixedChunks.forEach((chunk) => {
    const classMatches = chunk.matchAll(/\sclass="([^"]+)"/g);
    for (const match of classMatches) {
      const className = match?.[1];
      if (className && tendonDefaultAttributesByClass.has(className)) {
        referencedTendonDefaultClasses.add(className);
      }
    }
  });
  return Array.from(referencedTendonDefaultClasses)
    .map((className) => {
      const attrXml = Object.entries(tendonDefaultAttributesByClass.get(className) || {})
        .filter(([key, value]) => key && value !== undefined && value !== null)
        .sort(compareTendonDefaultAttr)
        .map(([key, value]) => ` ${key}="${xmlEscapeAttr(String(value))}"`)
        .join("");
      return `    <default class="${xmlEscapeAttr(className)}">\n      <tendon${attrXml}/>\n    </default>`;
    })
    .join("\n");
};

export const buildDefaultXml = ({
  bodyChildclassStubNames,
  fallbackDefaultXml,
  preservedDefaultBlockXml,
}: {
  bodyChildclassStubNames: Set<string>;
  fallbackDefaultXml: string;
  preservedDefaultBlockXml: string;
}): string => {
  const bodyChildclassStubXml = Array.from(bodyChildclassStubNames)
    .sort()
    .map((className) => `    <default class="${xmlEscapeAttr(className)}"/>`)
    .join("\n");
  const appendDefaultClassStubs = (defaultBlockXml: string): string => {
    if (!bodyChildclassStubXml) return defaultBlockXml;
    const closingDefaultPattern = /\n?\s*<\/default>\s*$/;
    if (closingDefaultPattern.test(defaultBlockXml)) {
      return defaultBlockXml.replace(
        closingDefaultPattern,
        `\n${bodyChildclassStubXml}\n  </default>`
      );
    }
    return `${defaultBlockXml}\n${bodyChildclassStubXml}`;
  };
  return preservedDefaultBlockXml && preservedDefaultBlockXml.length > 0
    ? `  ${appendDefaultClassStubs(preservedDefaultBlockXml)}\n`
    : `${appendDefaultClassStubs(fallbackDefaultXml)}\n`;
};
